import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarPlus, ChartLineUp, ChatCircle, Compass, CurrencyDollar, House, ListChecks, ShareNetwork, SignOut, Ticket, UserCircle, UsersThree, Wallet, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { startCheckout } from "../guest/Profile";
import { formatPlanPrice, intervalLabel, yearlySavings, BillingToggle, usePricing } from "../../hooks/usePricing";
import { PROMOTER_TITLES } from "../../lib/promoterTitles";
import WalletPanel from "../../components/WalletPanel";
import MediaUpload from "../../components/MediaUpload";
import MessagesPanel from "../../components/MessagesPanel";

const NAV = [
  { id: "overview", label: "Overview", icon: House },
  { id: "events", label: "Events", icon: CalendarPlus },
  { id: "guests", label: "Guest Lists", icon: ListChecks },
  { id: "earnings", label: "Earnings", icon: CurrencyDollar },
  { id: "team", label: "Promo Team", icon: UsersThree },
  { id: "profile", label: "Profile", icon: UserCircle },
  { id: "cards", label: "Share Cards", icon: ShareNetwork },
  { id: "messages", label: "Messages", icon: ChatCircle },
  { id: "wallet", label: "Wallet", icon: Wallet },
];

function Stat({ label, value, sub, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-3xl font-black ${accent}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function PromoterDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const pricing = usePricing();
  const [billing, setBilling] = useState("yearly");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [tips, setTips] = useState(null);

  useEffect(() => {
    if (tab === "wallet") API.get("/tips/received").then((r) => setTips(r.data)).catch(() => setTips({ total: 0, tips: [] }));
  }, [tab]);
  const [overview, setOverview] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [memberHandle, setMemberHandle] = useState({});
  const [venues, setVenues] = useState([]);
  const [guestlist, setGuestlist] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", venue_id: "", team_id: "", date_label: "Saturday", start: "10 PM", end: "2 AM", address: "", specials: "", cover: 20, list_perk: "Guest List Entry", payout_type: "paid_guest_only", payout_amount: 5, tonight: false, image_path: null, media: [] });
  const [pName, setPName] = useState("");
  const [pTitle, setPTitle] = useState("Party Promoter");
  const [pBio, setPBio] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setPName(user.name || "");
      setPTitle(user?.promoter?.title || "Party Promoter");
      setPBio(user.bio || "");
    }
  }, [user?.id]);

  const saveProfile = async () => {
    try {
      await API.patch("/me", { name: pName, promoter_title: pTitle, bio: pBio });
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveAvatar = async (m) => {
    if (!m) return;
    try {
      await API.patch("/me", { avatar_url: m.url });
      await refreshUser();
      toast.success("Photo updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const createTeam = async () => {
    if (!teamName.trim()) return;
    try {
      await API.post("/promoter/teams", { name: teamName.trim() });
      setTeamName("");
      load();
      toast.success("Team created");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addTeamMember = async (teamId) => {
    const handle = (memberHandle[teamId] || "").trim();
    if (!handle) return;
    try {
      await API.post(`/promoter/teams/${teamId}/members`, { handle });
      setMemberHandle((s) => ({ ...s, [teamId]: "" }));
      load();
      toast.success("Promoter added to team");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeTeamMember = async (teamId, memberId) => {
    try {
      await API.delete(`/promoter/teams/${teamId}/members/${memberId}`);
      load();
      toast.success("Removed from team");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteTeam = async (teamId) => {
    try {
      await API.delete(`/promoter/teams/${teamId}`);
      load();
      toast.success("Team deleted");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const handlePath = encodeURIComponent(user?.handle || "");

  const load = () => {
    API.get("/promoter/overview").then((r) => {
      setOverview(r.data);
      if (r.data.events.length && !selectedEvent) setSelectedEvent(r.data.events[0].event.id);
    }).catch(() => {});
    API.get("/venues").then((r) => setVenues(r.data)).catch(() => {});
    API.get("/promoter/teams").then((r) => setTeams(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    if (selectedEvent) API.get(`/promoter/events/${selectedEvent}/guestlist`).then((r) => setGuestlist(r.data)).catch(() => {});
  }, [selectedEvent]);

  const addGuest = async () => {
    try {
      const { data } = await API.post("/promoter/guestlist", { event_id: selectedEvent, email: guestEmail });
      toast.success(data.status === "added" ? `${data.guest.handle} added — pass delivered` : "Already on the list");
      setGuestEmail("");
      API.get(`/promoter/events/${selectedEvent}/guestlist`).then((r) => setGuestlist(r.data));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const payFee = async (eventId) => {
    try {
      const { data } = await API.post("/payments/event-fee/checkout", {
        event_id: eventId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(fmtErr(err));
      load();
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      const selectedVenue = venues.find((v) => v.id === form.venue_id);
      const { data: ev } = await API.post("/promoter/events", {
        ...form,
        address: form.address || selectedVenue?.address || "",
        cover: Number(form.cover),
        payout_amount: Number(form.payout_amount),
        venue_id: form.venue_id || null,
        team_id: form.team_id || null,
      });
      toast.success("Event created — complete the $5 publish fee to go live");
      setShowCreate(false);
      await payFee(ev.id);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const t = overview?.totals;
  const subscribed = user?.subscription?.status === "active";

  return (
    <div data-testid="promoter-dashboard" className="flex min-h-screen bg-[#050505] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <aside className="glass flex w-16 flex-col items-center gap-2 border-y-0 border-l-0 py-6 md:w-56 md:items-stretch md:px-4">
        <div className="mb-6 px-2">
          <img src="/logo.png" alt="NITEPASS" className="h-10 w-auto md:h-12" />
          <div className="mt-1 hidden text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 md:block">Promoter</div>
        </div>
        {NAV.map((n) => (
          <button key={n.id} data-testid={`promoter-nav-${n.id}`} onClick={() => setTab(n.id)}
            className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold transition-colors duration-200 ${tab === n.id ? "bg-neon-violet/15 text-neon-violet" : "text-zinc-500 hover:text-white"}`}>
            <n.icon size={20} /> <span className="hidden md:inline">{n.label}</span>
          </button>
        ))}
        <button data-testid="promoter-guest-app" onClick={() => navigate("/app")}
          className="mt-6 flex items-center gap-3 rounded-xl border border-neon-gold/25 px-3 py-3 text-sm font-bold text-neon-gold/80 transition-colors duration-200 hover:bg-neon-gold/10 hover:text-neon-gold">
          <Compass size={20} /> <span className="hidden md:inline">Guest App</span>
        </button>
        <p className="mt-2 hidden px-3 text-[10px] leading-relaxed text-zinc-600 md:block">Off the clock? Hit the city as a guest.</p>
        <button data-testid="promoter-logout" onClick={async () => { await logout(); navigate("/"); }}
          className="mt-auto flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-zinc-600 hover:text-red-400">
          <SignOut size={20} /> <span className="hidden md:inline">Log out</span>
        </button>
      </aside>

      <main className="flex-1 p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <button data-testid="promoter-back-btn" onClick={() => navigate("/")}
                className="glass rounded-full p-2 text-zinc-400 transition-colors hover:text-neon-violet">
                <ArrowLeft size={16} />
              </button>
              <div className="neon-overline" data-testid="promoter-title">{user?.handle} · {overview?.profile?.title || user?.promoter?.title || "Party Promoter"}</div>
            </div>
            <h1 className="mt-1 font-display text-2xl font-black">Promoter Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            {overview?.profile && (
              <div className="rounded-full border border-neon-yellow/40 bg-neon-yellow/10 px-4 py-2 text-xs font-black text-neon-yellow" data-testid="promoter-score">
                PROMOTER SCORE: {overview.profile.score}
              </div>
            )}
            <button data-testid="create-event-btn" onClick={() => setShowCreate(true)} className="btn-neon !bg-neon-violet !py-2 text-sm !text-black !shadow-glow-violet">+ New Event</button>
          </div>
        </div>

        {!subscribed && (
          <div className="mt-5 rounded-2xl border border-neon-gold/40 bg-neon-gold/5 p-4" data-testid="subscription-banner">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm"><span className="font-black">Activate NITEPASS PROMOTER</span> <span className="text-zinc-400">— {formatPlanPrice(pricing[`promoter_${billing}`])}/{intervalLabel(pricing[`promoter_${billing}`])} unlocks publishing & payouts.</span></div>
              <BillingToggle value={billing} onChange={setBilling} testid="promoter-billing-toggle"
                savings={yearlySavings(pricing.promoter_monthly, pricing.promoter_yearly)} />
            </div>
            <button data-testid="activate-subscription-btn" onClick={() => startCheckout(`promoter_${billing}`).catch((e) => toast.error(fmtErr(e)))}
              className="mt-3 rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold">Activate — {formatPlanPrice(pricing[`promoter_${billing}`])}/{billing === "monthly" ? "mo" : "yr"}</button>
          </div>
        )}

        {tab === "overview" && t && (
          <div className="mt-6" data-testid="promoter-overview">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Stat label="Guest List" value={t.list_count} accent="text-neon-violet" />
              <Stat label="Checked In" value={t.checked_in} sub={`${t.conversion}% conversion`} />
              <Stat label="Paid Entries" value={t.paid} accent="text-neon-green" />
              <Stat label="Free Entries" value={t.free} />
              <Stat label="Traffic Generated" value={`$${t.revenue.toLocaleString()}`} accent="text-neon-yellow" />
              <Stat label="Promoter Earnings" value={`$${t.earnings.toLocaleString()}`} accent="text-neon-gold" />
              <Stat label="Verified Guests" value={overview.profile.verified_guests?.toLocaleString()} />
              <Stat label="Events Hosted" value={overview.profile.events_hosted} sub={overview.profile.top_badge || ""} />
            </div>
            <p className="mt-4 text-xs text-zinc-500">Every guest-list QR carries your attribution. One scan = guest + event + you + venue.</p>
          </div>
        )}

        {tab === "events" && (
          <div className="mt-6 overflow-x-auto" data-testid="promoter-events-table">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="py-3 pr-4">Event</th><th className="pr-4">Venue</th><th className="pr-4">List</th>
                  <th className="pr-4">Checked In</th><th className="pr-4">Paid</th><th className="pr-4">Revenue</th><th>Payout</th>
                </tr>
              </thead>
              <tbody>
                {overview?.events.map((row) => (
                  <tr key={row.event.id} className="border-b border-white/5">
                    <td className="py-3 pr-4 font-bold">{row.event.title}
                      {row.event.status === "pending_payment" && (
                        <button data-testid={`pay-fee-${row.event.id}`} onClick={() => payFee(row.event.id)}
                          className="ml-2 rounded-full border border-neon-gold/40 bg-neon-gold/15 px-2 py-0.5 text-[10px] font-black text-neon-gold transition-colors duration-150 hover:bg-neon-gold hover:text-black">
                          $5 FEE DUE — PAY
                        </button>
                      )}
                      <div className="text-[11px] font-normal text-zinc-500">{row.event.date_label} · {row.event.start}{row.event.end ? `–${row.event.end}` : ""}</div>
                      {row.event.specials && <div className="text-[10px] font-normal text-neon-gold">{row.event.specials}</div>}</td>
                    <td className="pr-4 text-zinc-400">{row.venue_name}</td>
                    <td className="pr-4">{row.event.list_count}</td>
                    <td className="pr-4">{row.event.checked_in}</td>
                    <td className="pr-4 text-neon-green">{row.event.paid}</td>
                    <td className="pr-4">${row.revenue.toLocaleString()}</td>
                    <td className="font-bold text-neon-gold">${row.owed.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {overview?.events.length === 0 && <p className="py-10 text-center text-sm text-zinc-500">No events yet. Create your first one.</p>}
          </div>
        )}

        {tab === "guests" && (
          <div className="mt-6 max-w-3xl" data-testid="guestlist-panel">
            <div className="flex flex-wrap gap-3">
              <select data-testid="guestlist-event-select" value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none">
                {overview?.events.map((row) => <option key={row.event.id} value={row.event.id}>{row.event.title} — {row.venue_name}</option>)}
              </select>
              <div className="flex flex-1 gap-2">
                <input data-testid="guest-email-input" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="Guest email — pass is delivered instantly" type="email"
                  className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-violet" />
                <button data-testid="add-guest-btn" onClick={addGuest} className="rounded-full bg-neon-violet px-5 py-2 text-xs font-black text-black">Add Guest</button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {guestlist.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm">
                  <div>
                    <span className="font-bold">{p.guest?.name || "Guest"}</span>
                    <span className="ml-2 text-xs text-neon-violet">{p.guest?.handle}</span>
                    <div className="text-[11px] text-zinc-500">{p.guest?.email}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${p.status === "used" ? "bg-neon-green/15 text-neon-green" : "bg-white/10 text-zinc-400"}`}>
                    {p.status === "used" ? "Checked in" : "QR Ready"}
                  </span>
                </div>
              ))}
              {guestlist.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No one on this list yet. Add your first guest.</p>}
            </div>
          </div>
        )}

        {tab === "earnings" && (
          <div className="mt-6 max-w-3xl space-y-3" data-testid="earnings-panel">
            {overview?.events.map((row) => (
              <div key={row.event.id} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
                <div className="flex items-center justify-between">
                  <div className="font-display font-black">{row.event.title}</div>
                  <div className="font-display text-xl font-black text-neon-gold">${row.owed.toLocaleString()}</div>
                </div>
                <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
                  <div><div className="text-zinc-500">Invited</div><div className="font-bold">{row.event.list_count}</div></div>
                  <div><div className="text-zinc-500">Arrived</div><div className="font-bold">{row.event.checked_in}</div></div>
                  <div><div className="text-zinc-500">Paid</div><div className="font-bold text-neon-green">{row.event.paid}</div></div>
                  <div><div className="text-zinc-500">Door Revenue</div><div className="font-bold text-neon-yellow">${row.revenue.toLocaleString()}</div></div>
                </div>
                <div className="mt-2 text-[11px] text-zinc-500">Deal: {row.model.type.replace(/_/g, " ")} {row.model.type.includes("share") ? `${row.model.amount * 100}%` : `$${row.model.amount}`}</div>
              </div>
            ))}
            <div className="rounded-2xl border border-neon-gold/40 bg-neon-gold/5 p-5 text-center">
              <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Total owed to you</div>
              <div className="font-display text-4xl font-black text-neon-gold" data-testid="total-earnings">${t?.earnings.toLocaleString()}</div>
              <div className="mt-1 text-[11px] text-zinc-500">Calculated from verified scans. Same record the venue sees.</div>
            </div>
          </div>
        )}
        {tab === "profile" && (
          <div className="mt-6 max-w-2xl" data-testid="promoter-profile-tab">
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
              <div className="font-display text-lg font-black">Public Promoter Profile</div>
              <p className="mt-1 text-xs text-zinc-500">
                Live at <span className="font-bold text-neon-violet">mynitepass.com/{user?.handle}</span> — guests and venues see this.
              </p>
              <div className="mt-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Profile photo</div>
                <div className="mt-2 flex items-center gap-4">
                  {user?.avatar_url && (
                    <img src={mediaUrl(user.avatar_url)} alt="" className="h-16 w-16 rounded-full border border-neon-gold/40 object-cover" data-testid="promoter-avatar-preview" />
                  )}
                  <MediaUpload imageOnly clearAfterUpload label="Upload photo" onUploaded={saveAvatar} />
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Display name
                  <input data-testid="promoter-name-input" value={pName} onChange={(e) => setPName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-violet" />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Title
                  <select data-testid="promoter-title-select" value={pTitle} onChange={(e) => setPTitle(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none">
                    {PROMOTER_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Description
                <textarea data-testid="promoter-bio-input" value={pBio} onChange={(e) => setPBio(e.target.value)} maxLength={500} rows={3}
                  placeholder="Tell venues and guests what kind of nights you create…"
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm normal-case outline-none focus:border-neon-violet" />
              </label>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] text-zinc-600">{pBio.length}/500</span>
                <div className="flex gap-2">
                  <button data-testid="promoter-view-public-btn" onClick={() => navigate(`/${user?.handle}`)}
                    className="rounded-full border border-neon-violet/40 px-4 py-1.5 text-[11px] font-bold text-neon-violet">View Public Page</button>
                  <button data-testid="promoter-save-profile-btn" onClick={saveProfile}
                    className="rounded-full bg-neon-gold px-4 py-1.5 text-[11px] font-black text-black">Save Profile</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "cards" && (
          <div className="mt-6 max-w-2xl space-y-4" data-testid="promoter-cards-tab">
            <div className="rounded-3xl border border-neon-violet/30 bg-[#0a0612] p-6">
              <div className="font-display text-lg font-black">Share Cards</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Post these to Instagram. Signups through your link are auto-tagged to you —
                {" "}{overview?.referral_signups || 0} total · {overview?.referral_handle_signups || 0} via your @handle card.
              </p>
              <div className="mt-4 space-y-3">
                {[
                  { key: "guestlist", title: "Guest List Card", desc: "Story format — drives guest list signups with your @handle link.", testid: "card-download-guestlist" },
                  { key: "card", title: "Profile Share Card", desc: "Feed post — your photo, title, score and profile link.", testid: "card-download-share" },
                  { key: "recap", title: "Last Night Recap", desc: "Proof of motion — guests, paid entries and revenue from last night.", testid: "card-download-recap" },
                ].map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/40 p-4">
                    <div>
                      <div className="text-sm font-black">{c.title}</div>
                      <div className="mt-0.5 text-[11px] text-zinc-500">{c.desc}</div>
                    </div>
                    <a data-testid={c.testid} href={mediaUrl(`/api/promoters/handle/${handlePath}/${c.key === "card" ? "card" : c.key}.png`)}
                      className="shrink-0 rounded-full bg-neon-gold px-4 py-2 text-[11px] font-black text-black shadow-glow-gold">
                      Download
                    </a>
                    {c.key === "guestlist" && (
                      <button data-testid="card-copy-link"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${window.location.origin}/api/share/promoter/${handlePath}`);
                            toast.success("Share link copied — unfurls with your card in iMessage & socials");
                          } catch { toast.error("Copy failed"); }
                        }}
                        className="shrink-0 rounded-full border border-neon-violet/40 px-3 py-2 text-[11px] font-bold text-neon-violet">
                        Copy Link
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "team" && (
          <div className="mt-6 max-w-2xl space-y-4" data-testid="promo-team-panel">
            <div className="rounded-3xl border border-neon-violet/25 bg-[#0a0612] p-6">
              <div className="font-display text-lg font-black">Promo Teams</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Team up to move bigger crowds: attach a team to an event and every verified guest scanned
                counts toward <span className="text-neon-violet">every team member's</span> stats and score.
              </p>
              <div className="mt-4 flex gap-2">
                <input data-testid="team-name-input" value={teamName} onChange={(e) => setTeamName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createTeam()}
                  placeholder="Team name (e.g. Southtown Squad)"
                  className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-violet" />
                <button data-testid="team-create-btn" onClick={createTeam} disabled={!teamName.trim()}
                  className="rounded-full bg-neon-violet px-5 py-2.5 text-xs font-black text-black disabled:opacity-40">Create Team</button>
              </div>
            </div>

            {teams.length === 0 ? (
              <p className="py-4 text-sm text-zinc-600">No teams yet. Create one and add promoters by @handle.</p>
            ) : teams.map((t) => (
              <div key={t.id} className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6" data-testid={`team-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-base font-black">{t.name}
                    <span className="ml-2 text-[11px] font-bold text-zinc-500">{t.members.length} member{t.members.length !== 1 ? "s" : ""}</span>
                  </div>
                  {t.owner_id === user?.id && (
                    <button data-testid={`team-delete-${t.id}`} onClick={() => deleteTeam(t.id)}
                      className="text-[11px] font-bold text-zinc-500 transition-colors hover:text-red-400">Delete</button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.members.map((m) => (
                    <span key={m.id} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-bold" data-testid={`team-member-${m.handle}`}>
                      {m.handle}{m.owner && <span className="text-neon-gold">· owner</span>}
                      {t.owner_id === user?.id && !m.owner && (
                        <button data-testid={`team-remove-${m.handle}`} onClick={() => removeTeamMember(t.id, m.id)}
                          className="text-zinc-500 hover:text-red-400">×</button>
                      )}
                    </span>
                  ))}
                </div>
                {t.owner_id === user?.id && (
                  <div className="mt-3 flex gap-2">
                    <input data-testid={`team-member-input-${t.id}`} value={memberHandle[t.id] || ""}
                      onChange={(e) => setMemberHandle((s) => ({ ...s, [t.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && addTeamMember(t.id)}
                      placeholder="Add promoter by @handle"
                      className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-2 text-sm outline-none focus:border-neon-violet" />
                    <button data-testid={`team-add-${t.id}`} onClick={() => addTeamMember(t.id)}
                      className="rounded-full border border-neon-violet/40 px-4 py-2 text-xs font-bold text-neon-violet">Add</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "messages" && <div className="mt-6"><MessagesPanel userRole="promoter" /></div>}

        {tab === "wallet" && (
          <div className="mt-6 max-w-4xl">
            <WalletPanel />
            <div className="mt-6 rounded-2xl border border-neon-gold/30 bg-[#0a0905] p-5" data-testid="tips-panel">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Tips from guests</div>
                {tips && <div className="font-display text-xl font-black text-neon-gold" data-testid="tips-total">${tips.total.toFixed(2)}</div>}
              </div>
              <div className="mt-3 space-y-2" data-testid="tips-list">
                {tips === null ? (
                  <p className="text-xs text-zinc-600">Loading…</p>
                ) : tips.tips.length === 0 ? (
                  <p className="text-xs text-zinc-600">No tips yet — guests can tip you from your public page after a great night.</p>
                ) : tips.tips.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                    <span className="text-xs font-bold">{t.note}</span>
                    <span className="text-xs font-black text-neon-green">+${t.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {showCreate && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="create-event-modal">
          <form onSubmit={createEvent} className="glass max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl p-6">
            <h3 className="font-display text-lg font-black">New Event</h3>
            <p className="text-[11px] text-zinc-500">Publishing an event carries a <span className="font-bold text-neon-gold">$5 platform fee</span> — your event goes live right after payment.</p>
            <input data-testid="event-title-input" required placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-violet" />
            <select data-testid="event-venue-select" required value={form.venue_id} onChange={(e) => setForm({ ...form, venue_id: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none">
              <option value="">Select venue</option>
              {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {teams.length > 0 && (
              <select data-testid="event-team-select" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none">
                <option value="">No promo team — solo event</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.members.length} promoters)</option>)}
              </select>
            )}
            <div className="grid grid-cols-3 gap-3">
              <input placeholder="Date" value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input placeholder="Opens" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input placeholder="Closes" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min="0" placeholder="Cover $" value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
              <input placeholder="List perk" value={form.list_perk} onChange={(e) => setForm({ ...form, list_perk: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            </div>
            <input placeholder="Event address (defaults to venue address)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" data-testid="event-address-input" />
            <input placeholder="Special drinks / offers (optional)" value={form.specials} onChange={(e) => setForm({ ...form, specials: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" data-testid="event-specials-input" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.payout_type} onChange={(e) => setForm({ ...form, payout_type: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none">
                <option value="per_guest">$ per verified guest</option>
                <option value="paid_guest_only">$ per paid guest</option>
                <option value="revenue_share">Revenue share (0.2 = 20%)</option>
              </select>
              <input type="number" step="0.01" min="0" placeholder="Payout amount" value={form.payout_amount} onChange={(e) => setForm({ ...form, payout_amount: e.target.value })}
                className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none" />
            </div>
            <MediaUpload onUploaded={(m) => setForm((f) => ({
              ...f,
              image_path: m && m.content_type.startsWith("image/") ? m.path : null,
              media: m && m.content_type.startsWith("video/") ? [m] : [],
            }))} />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" checked={form.tonight} onChange={(e) => setForm({ ...form, tonight: e.target.checked })} /> Happening tonight
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost-neon flex-1 !py-2.5 text-sm">Cancel</button>
              <button data-testid="event-submit-btn" type="submit" className="btn-neon flex-1 !py-2.5 text-sm">Create & Pay $5 Fee</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
